/* eslint-disable no-undef */
import { initializeApp } from 'firebase/app';
import {
  initializeAuth,
  connectAuthEmulator,
  browserLocalPersistence,
  browserSessionPersistence,
  indexedDBLocalPersistence,
} from 'firebase/auth';
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions';
import {
  connectFirestoreEmulator,
  initializeFirestore,
  memoryLocalCache,
  collection,
  doc,
} from 'firebase/firestore';
import { CANONICAL_APP_ID, dataRootSegments } from './dataPaths';

const fallbackFirebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

const firebaseConfig =
  typeof __firebase_config !== 'undefined'
    ? JSON.parse(__firebase_config)
    : fallbackFirebaseConfig;

const app = initializeApp(firebaseConfig);
/**
 * getAuth() popup/yönlendirme çözümleyicisini de kurar; Safari ve iOS'ta bu, oturum başlamadan önce
 * apis.google.com betiğini ve authDomain iframe'ini yükletip bekletir. Popup/yönlendirme girişi
 * kullanılmadığı için çözümleyici verilmez.
 */
export const auth = initializeAuth(app, {
  persistence: [browserLocalPersistence, indexedDBLocalPersistence, browserSessionPersistence],
});

/**
 * Bellek içi önbellek her tarayıcıda: eğitmen öğrenci başına bir program dinleyicisi açar
 * (200 öğrencide 200+ hedef). IndexedDB kalıcı önbelleği her hedefi ve belgeyi sırayla diske
 * yazdığı için Chromium'da ilk veriler ~10 sn, WebKit'te onlarca saniye gecikiyordu (bellek içi: ~2,5 sn).
 */
export const db = initializeFirestore(app, {
  localCache: memoryLocalCache(),
  experimentalAutoDetectLongPolling: true,
});
/** Callable Functions — e-posta OTP (europe-west1) */
export const functions = getFunctions(app, 'europe-west1');

/** Yalnızca yerel yük testi: `REACT_APP_USE_EMULATORS=true npm start` (canlı derlemede kapalı). */
if (process.env.REACT_APP_USE_EMULATORS === 'true') {
  connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true });
  connectFirestoreEmulator(db, '127.0.0.1', 8080);
  connectFunctionsEmulator(functions, '127.0.0.1', 5001);
}

/** Geriye dönük uyumluluk */
export const safeAppId = CANONICAL_APP_ID;

export const initialAuthToken =
  typeof __initial_auth_token !== 'undefined' ? __initial_auth_token : null;

// ─── Firestore path yardımcıları ────────────────────────────────────────────
// Tüm uygulama kodu yalnızca bu yardımcıları kullanmalıdır (tek kanonik yol).

const dataRoot = () => dataRootSegments(CANONICAL_APP_ID);

export const STUDENT_SUBCOLLECTIONS = [
  'instructorReviews',
  'attendance', // yalnızca hesap silme / belge birleştirmede; yeni kayıt yazılmaz
  'swimmingRecords',
  'programs',
];

export const collectionRef = (name) => collection(db, ...dataRoot(), name);

export const docRef = (collectionName, docId) =>
  doc(db, ...dataRoot(), collectionName, docId);

export const studentDoc = (studentId) => docRef('students', studentId);
export const instructorDoc = (instructorId) => docRef('instructors', instructorId);
export const notificationDoc = (notificationId) => docRef('notifications', notificationId);
export const consentDoc = (consentId) => docRef('consents', consentId);
export const paymentDoc = (paymentId) => docRef('payments', paymentId);

export const studentSubCol = (studentId, subName) =>
  collection(db, ...dataRoot(), 'students', studentId, subName);

export const studentSubDoc = (studentId, subName, docId) =>
  doc(db, ...dataRoot(), 'students', studentId, subName, docId);
